import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import useAuthStore from '../store/authStore';
import api from '../api/offlineClient';
import { uploadAvatar } from '../utils/avatarUpload';

const EMPTY = {
  nombre: '', numero_empleado: '', dni: '', telefono: '',
  telefono_empresa: '', email: '', direccion: '', estado: 'activo', saldo_inicial: '', genero: 'm',
  foto_url: '', foto_public_id: '',
};

export default function Empleados() {
  const user = useAuthStore(s => s.user);
  const navigate = useNavigate();
  const [emps, setEmps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [sedeTab, setSedeTab] = useState('todas');
  const [modal, setModal] = useState(null);
  const [saving, setSaving] = useState(false);
  const [newLogin, setNewLogin] = useState(null);
  const [carteraOpen, setCarteraOpen] = useState({});
  const [abonos, setAbonos] = useState({});
  const [balances, setBalances] = useState({});
  const [abonoForm, setAbonoForm] = useState({});
  const [avatarUploading, setAvatarUploading] = useState(false);
  const avatarInputRef = useRef();

  useEffect(() => {
    if (user && !['admin','jefe','administrativo'].includes(user.rol)) navigate('/');
  }, [user, navigate]);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try { setEmps((await api.get('/api/employees/list')).data || []); }
    catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = emps.filter(e => {
    const matchSearch = [e.nombre, e.dni, e.email, String(e.numero_empleado || '')]
      .some(v => v && v.toLowerCase().includes(search.toLowerCase()));
    const matchSede = sedeTab === 'todas' || (e.sede || 'sevilla') === sedeTab;
    return matchSearch && matchSede;
  });

  async function save() {
    setSaving(true);
    try {
      const d = modal.data;
      if (modal.mode === 'add') {
        const r = await api.post('/api/employees/create', d);
        setModal(null);
        await load();
        if (r.nuevo_login) setNewLogin(r.nuevo_login);
      } else {
        await api.put(`/api/employees/${d.id}`, d);
        setModal(null); await load();
      }
    } catch (e) { alert('Error: ' + e.message); }
    finally { setSaving(false); }
  }

  async function del(emp) {
    if (!window.confirm(`¿Eliminar a ${emp.nombre}?`)) return;
    try { await api.delete(`/api/employees/${emp.id}`); await load(); }
    catch (e) { alert('Error: ' + e.message); }
  }

  function setField(field, val) {
    setModal(m => ({ ...m, data: { ...m.data, [field]: val } }));
  }

  async function handleModalAvatar(e) {
    const file = e.target.files[0];
    e.target.value = '';
    if (!file) return;
    setAvatarUploading(true);
    try {
      const { foto_url, foto_public_id } = await uploadAvatar(file);
      setModal(m => ({ ...m, data: { ...m.data, foto_url, foto_public_id } }));
    } catch (err) {
      alert('No se pudo subir la foto: ' + err.message);
    } finally {
      setAvatarUploading(false);
    }
  }

  async function toggleCartera(empId) {
    const open = !carteraOpen[empId];
    setCarteraOpen(prev => ({ ...prev, [empId]: open }));
    if (open && !abonos[empId]) {
      try {
        const [abonosRes, balRes] = await Promise.all([
          api.get(`/api/employees/${empId}/abonos`),
          api.get(`/api/employees/${empId}/cartera-balance`),
        ]);
        setAbonos(prev => ({ ...prev, [empId]: abonosRes.data || [] }));
        setBalances(prev => ({ ...prev, [empId]: balRes.data }));
      } catch { setAbonos(prev => ({ ...prev, [empId]: [] })); }
    }
  }

  async function refreshBalance(empId) {
    try {
      const r = await api.get(`/api/employees/${empId}/cartera-balance`);
      setBalances(prev => ({ ...prev, [empId]: r.data }));
    } catch {}
  }

  async function addAbono(empId) {
    const f = abonoForm[empId] || {};
    if (!f.importe) return;
    try {
      const r = await api.post(`/api/employees/${empId}/abonos`, {
        concepto: f.concepto || '', importe: f.importe, fecha: f.fecha || undefined
      });
      setAbonos(prev => ({ ...prev, [empId]: [r.data, ...(prev[empId] || [])] }));
      setAbonoForm(prev => ({ ...prev, [empId]: {} }));
      refreshBalance(empId);
    } catch (e) { alert('Error: ' + e.message); }
  }

  async function delAbono(empId, abonoId) {
    if (!window.confirm('¿Eliminar este abono?')) return;
    try {
      await api.delete(`/api/employees/abonos/${abonoId}`);
      setAbonos(prev => ({ ...prev, [empId]: prev[empId].filter(a => a.id !== abonoId) }));
      refreshBalance(empId);
    } catch (e) { alert('Error: ' + e.message); }
  }

  return (
    <div style={{ padding: 'clamp(12px,3vw,24px)', maxWidth: 1100, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18, flexWrap: 'wrap', gap: 10 }}>
        <div>
          <div style={{ fontSize: 20, fontWeight: 800, color: 'var(--text)' }}>Empleados</div>
          <div style={{ fontSize: 12, color: 'var(--text-dim)', marginTop: 2 }}>{emps.length} registrados</div>
        </div>
        <button style={BTN_PRI} onClick={() => setModal({ mode: 'add', data: { ...EMPTY } })}>+ Añadir</button>
      </div>

      <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
        {['todas','sevilla','granada'].map(s => (
          <button key={s} onClick={() => setSedeTab(s)} style={{
            padding: '6px 16px', borderRadius: 7, fontWeight: 800, fontSize: 12, cursor: 'pointer',
            textTransform: 'capitalize',
            background: sedeTab === s ? 'var(--amber)' : 'var(--surface2)',
            color: sedeTab === s ? '#000' : 'var(--text-dim)',
            border: sedeTab === s ? 'none' : '1px solid var(--border)',
          }}>{s.charAt(0).toUpperCase()+s.slice(1)}</button>
        ))}
      </div>

      <input
        placeholder="Buscar nombre, DNI, email o nº empleado…"
        value={search} onChange={e => setSearch(e.target.value)}
        style={{ ...INP, marginBottom: 16, maxWidth: 400 }}
      />

      {error && <div style={{ color: '#f87171', marginBottom: 12, fontSize: 13 }}>{error}</div>}

      {loading ? (
        <div style={{ color: 'var(--text-dim)', padding: 32, textAlign: 'center' }}>Cargando…</div>
      ) : filtered.length === 0 ? (
        <div style={{ color: 'var(--text-dim)', padding: 32, textAlign: 'center' }}>Sin resultados</div>
      ) : (
        <div style={{ display: 'grid', gap: 10 }}>
          {filtered.map(emp => (
            <div key={emp.id} style={CARD}>
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                <div style={{ flex: 1, minWidth: 200, display: 'flex', gap: 12 }}>
                  <div style={{
                    width: 42, height: 42, borderRadius: '50%', overflow: 'hidden', flexShrink: 0,
                    background: 'var(--amber)', color: '#000', display: 'flex', alignItems: 'center',
                    justifyContent: 'center', fontWeight: 900, fontSize: 16,
                  }}>
                    {emp.foto_url
                      ? <img src={emp.foto_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      : (emp.nombre || '?').charAt(0).toUpperCase()}
                  </div>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                      <span style={{ fontSize: 15, fontWeight: 800, color: 'var(--text)' }}>{emp.nombre}</span>
                      <span style={{
                        fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 4,
                        background: emp.estado === 'activo' ? 'rgba(74,222,128,.12)' : 'rgba(245,158,11,.1)',
                        color: emp.estado === 'activo' ? '#4ade80' : '#f59e0b',
                        border: `1px solid ${emp.estado === 'activo' ? 'rgba(74,222,128,.4)' : 'rgba(245,158,11,.4)'}`
                      }}>{emp.estado}</span>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(190px,1fr))', gap: '4px 14px' }}>
                      {emp.numero_empleado != null && <Info icon="🔢" label="Nº empleado"   val={emp.numero_empleado} />}
                      {emp.dni             && <Info icon="🆔" label="DNI"          val={emp.dni} />}
                      {emp.telefono        && <Info icon="📱" label="Tel. personal" val={emp.telefono} />}
                      {emp.telefono_empresa && <Info icon="☎️" label="Tel. empresa"  val={emp.telefono_empresa} />}
                      {emp.email           && <Info icon="✉️" label="Email"         val={emp.email} />}
                      {emp.direccion       && <Info icon="📍" label="Dirección"     val={emp.direccion} />}
                    </div>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 7, flexShrink: 0 }}>
                  <button style={BTN_SEC} onClick={() => setModal({ mode: 'edit', data: { ...emp } })}>✏️ Editar</button>
                  <button style={{ ...BTN_SEC, color: '#4ade80', borderColor: 'rgba(74,222,128,.4)' }}
                    onClick={() => toggleCartera(emp.id)}>
                    💼 Cartera {carteraOpen[emp.id] ? '▲' : '▼'}
                  </button>
                  <button style={BTN_DEL} onClick={() => del(emp)}>🗑</button>
                </div>
              </div>

              {carteraOpen[emp.id] && (
                <div style={{ marginTop: 12, borderTop: '1px solid var(--border)', paddingTop: 12 }}>
                  <div style={{ fontSize: 11, fontWeight: 800, color: '#4ade80', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 8 }}>
                    Cartera — {emp.nombre}
                  </div>
                  {balances[emp.id] && (() => {
                    const b = balances[emp.id];
                    return (
                      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 12, padding: '10px 12px',
                        background: 'var(--surface-deep)', borderRadius: 8, border: '1px solid var(--border)' }}>
                        <BalItem label="Saldo inicial" val={b.saldo_inicial} color="var(--text-dim)" />
                        <span style={{ color: 'var(--border)', alignSelf: 'center' }}>+</span>
                        <BalItem label="Abonos" val={b.total_abonos} color="#4ade80" />
                        <span style={{ color: 'var(--border)', alignSelf: 'center' }}>−</span>
                        <BalItem label="Notas gastos" val={b.total_notas_gastos} color="#f87171" />
                        <span style={{ color: 'var(--border)', alignSelf: 'center' }}>−</span>
                        <BalItem label="Facturas aprobadas" val={b.total_facturas_aprobadas} color="#f87171" />
                        <span style={{ color: 'var(--border)', alignSelf: 'center' }}>=</span>
                        <BalItem label="Saldo actual" val={b.balance} color={b.balance >= 0 ? '#4ade80' : '#f87171'} bold />
                      </div>
                    );
                  })()}
                  {/* Formulario añadir */}
                  <div style={{ display: 'flex', gap: 6, marginBottom: 10, flexWrap: 'wrap' }}>
                    <input placeholder="Concepto" value={(abonoForm[emp.id] || {}).concepto || ''}
                      onChange={e => setAbonoForm(p => ({ ...p, [emp.id]: { ...(p[emp.id] || {}), concepto: e.target.value } }))}
                      style={{ ...INP, flex: '2 1 140px', padding: '6px 8px', fontSize: 12 }} />
                    <input placeholder="Importe €" type="number" step="0.01" min="0"
                      value={(abonoForm[emp.id] || {}).importe || ''}
                      onChange={e => setAbonoForm(p => ({ ...p, [emp.id]: { ...(p[emp.id] || {}), importe: e.target.value } }))}
                      style={{ ...INP, flex: '1 1 90px', padding: '6px 8px', fontSize: 12 }} />
                    <input type="date" value={(abonoForm[emp.id] || {}).fecha || ''}
                      onChange={e => setAbonoForm(p => ({ ...p, [emp.id]: { ...(p[emp.id] || {}), fecha: e.target.value } }))}
                      style={{ ...INP, flex: '1 1 110px', padding: '6px 8px', fontSize: 12 }} />
                    <button style={{ ...BTN_PRI, padding: '6px 14px', fontSize: 12 }}
                      onClick={() => addAbono(emp.id)}
                      disabled={!(abonoForm[emp.id] || {}).importe}>
                      + Añadir
                    </button>
                  </div>
                  {/* Lista abonos */}
                  {(abonos[emp.id] || []).length === 0 ? (
                    <div style={{ fontSize: 12, color: 'var(--text-dim)' }}>Sin abonos registrados</div>
                  ) : (
                    <div style={{ display: 'grid', gap: 4 }}>
                      {(abonos[emp.id] || []).map(ab => (
                        <div key={ab.id} style={{ display: 'flex', alignItems: 'center', gap: 8,
                          background: 'var(--surface-deep)', borderRadius: 6, padding: '6px 10px',
                          border: '1px solid var(--border)' }}>
                          <span style={{ fontSize: 12, color: '#4ade80', fontWeight: 800, minWidth: 64 }}>
                            +{parseFloat(ab.importe).toFixed(2)} €
                          </span>
                          <span style={{ fontSize: 11, color: 'var(--text)', flex: 1 }}>{ab.concepto || '—'}</span>
                          <span style={{ fontSize: 10, color: 'var(--text-dim)', whiteSpace: 'nowrap' }}>
                            {ab.fecha ? new Date(ab.fecha).toLocaleDateString('es-ES') : '—'}
                          </span>
                          <button style={{ ...BTN_DEL, padding: '3px 7px', fontSize: 12 }}
                            onClick={() => delAbono(emp.id, ab.id)}>🗑</button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {newLogin && (
        <div style={OVERLAY} onClick={e => e.target === e.currentTarget && setNewLogin(null)}>
          <div style={{ ...MODAL, maxWidth: 380, textAlign: 'center' }}>
            <div style={{ fontSize: 28, marginBottom: 8 }}>🔑</div>
            <div style={{ fontSize: 14, fontWeight: 800, color: 'var(--amber)', marginBottom: 6 }}>
              Acceso creado
            </div>
            <div style={{ fontSize: 11, color: 'var(--text-dim)', marginBottom: 16 }}>
              Apúntalo ahora — la contraseña no se podrá volver a ver.
            </div>
            <div style={{ display: 'grid', gap: 10, marginBottom: 16 }}>
              <div style={{ background: 'var(--surface-deep)', border: '1px solid var(--border)', borderRadius: 8, padding: '10px 12px' }}>
                <div style={LBL}>Usuario</div>
                <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)', fontFamily: 'monospace' }}>{newLogin.email}</div>
              </div>
              <div style={{ background: 'var(--surface-deep)', border: '1px solid var(--border)', borderRadius: 8, padding: '10px 12px' }}>
                <div style={LBL}>Contraseña</div>
                <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)', fontFamily: 'monospace' }}>{newLogin.password}</div>
              </div>
            </div>
            <button style={{ ...BTN_PRI, width: '100%' }} onClick={() => setNewLogin(null)}>Entendido</button>
          </div>
        </div>
      )}

      {modal && (
        <div style={OVERLAY} onClick={e => e.target === e.currentTarget && setModal(null)}>
          <div style={MODAL}>
            <div style={{ fontSize: 14, fontWeight: 800, color: 'var(--amber)', marginBottom: 16 }}>
              {modal.mode === 'add' ? 'Añadir empleado' : 'Editar empleado'}
            </div>

            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 16 }}>
              <div
                onClick={() => !avatarUploading && avatarInputRef.current?.click()}
                title="Cambiar foto"
                style={{
                  position: 'relative', width: 72, height: 72, borderRadius: '50%',
                  background: 'var(--amber)', color: '#000', fontSize: 26, fontWeight: 900,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  overflow: 'hidden', cursor: avatarUploading ? 'wait' : 'pointer',
                  border: '2px solid var(--border)',
                }}>
                {avatarUploading ? (
                  <span style={{ fontSize: 12 }}>…</span>
                ) : modal.data.foto_url ? (
                  <img src={modal.data.foto_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                ) : (
                  (modal.data.nombre || '?').charAt(0).toUpperCase()
                )}
                <div style={{
                  position: 'absolute', bottom: -1, right: -1, width: 22, height: 22, borderRadius: '50%',
                  background: 'var(--surface2)', border: '1px solid var(--border)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11,
                }}>📷</div>
              </div>
              <input ref={avatarInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleModalAvatar} />
            </div>

            <Fld label="Nombre completo *">
              <input style={INP} value={modal.data.nombre} onChange={e => setField('nombre', e.target.value)} />
            </Fld>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <Fld label="Nº empleado">
                <input style={INP} type="number" value={modal.data.numero_empleado} onChange={e => setField('numero_empleado', e.target.value)} />
              </Fld>
              <Fld label="DNI">
                <input style={INP} value={modal.data.dni} onChange={e => setField('dni', e.target.value)} />
              </Fld>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <Fld label="Tel. personal">
                <input style={INP} type="tel" value={modal.data.telefono} onChange={e => setField('telefono', e.target.value)} />
              </Fld>
              <Fld label="Tel. empresa">
                <input style={INP} type="tel" value={modal.data.telefono_empresa} onChange={e => setField('telefono_empresa', e.target.value)} />
              </Fld>
            </div>
            <Fld label="Email">
              <input style={INP} type="email" value={modal.data.email} onChange={e => setField('email', e.target.value)} />
            </Fld>
            <Fld label="Dirección">
              <input style={INP} value={modal.data.direccion} onChange={e => setField('direccion', e.target.value)} />
            </Fld>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <Fld label="Estado">
                <select style={INP} value={modal.data.estado} onChange={e => setField('estado', e.target.value)}>
                  <option value="activo">Activo</option>
                  <option value="inactivo">Inactivo</option>
                </select>
              </Fld>
              <Fld label="Sede">
                <select style={INP} value={modal.data.sede || 'sevilla'} onChange={e => setField('sede', e.target.value)}>
                  <option value="sevilla">Sevilla</option>
                  <option value="granada">Granada</option>
                </select>
              </Fld>
            </div>
            <Fld label="Género">
              <select style={INP} value={modal.data.genero || 'm'} onChange={e => setField('genero', e.target.value)}>
                <option value="m">Masculino</option>
                <option value="f">Femenino</option>
              </select>
            </Fld>
            <Fld label="Saldo inicial Cartera Segavan (€)">
              <input style={INP} type="number" min="0" step="0.01"
                value={modal.data.saldo_inicial ?? ''}
                onChange={e => setField('saldo_inicial', e.target.value)}
                placeholder="0.00" />
            </Fld>

            <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
              <button style={{ ...BTN_PRI, flex: 1 }} onClick={save} disabled={saving || !modal.data.nombre}>
                {saving ? 'Guardando…' : 'Guardar'}
              </button>
              <button style={BTN_SEC} onClick={() => setModal(null)}>Cancelar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function BalItem({ label, val, color, bold }) {
  return (
    <div style={{ textAlign: 'center' }}>
      <div style={{ fontSize: 9, color: 'var(--text-dim)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: 13, fontWeight: bold ? 900 : 700, color }}>{parseFloat(val || 0).toFixed(2)} €</div>
    </div>
  );
}

function Info({ icon, label, val }) {
  return (
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 5, minWidth: 0 }}>
      <span style={{ fontSize: 11, flexShrink: 0 }}>{icon}</span>
      <span style={{ fontSize: 10, color: 'var(--text-dim)', minWidth: 76, flexShrink: 0 }}>{label}:</span>
      <span style={{ fontSize: 11, color: 'var(--text)', fontWeight: 600, minWidth: 0, overflowWrap: 'break-word' }}>{val}</span>
    </div>
  );
}

function Fld({ label, children }) {
  return (
    <div style={{ marginBottom: 10 }}>
      <label style={LBL}>{label}</label>
      {children}
    </div>
  );
}

const INP = { background: 'var(--surface-deep)', border: '1.5px solid var(--border)', borderRadius: 6, color: 'var(--text)', padding: '8px 10px', width: '100%', fontSize: 13, fontFamily: 'inherit', boxSizing: 'border-box' };
const LBL = { fontSize: 10, color: 'var(--text-dim)', fontWeight: 700, letterSpacing: '.05em', textTransform: 'uppercase', display: 'block', marginBottom: 4 };
const BTN_PRI = { background: 'var(--amber)', color: '#000', border: 'none', padding: '9px 18px', borderRadius: 7, cursor: 'pointer', fontWeight: 800, fontSize: 13 };
const BTN_SEC = { background: 'var(--surface2)', color: 'var(--text-dim)', border: '1px solid var(--border)', padding: '8px 14px', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 600 };
const BTN_DEL = { background: 'rgba(248,113,113,.12)', color: '#f87171', border: '1px solid rgba(248,113,113,.4)', padding: '8px 10px', borderRadius: 6, cursor: 'pointer', fontSize: 13 };
const CARD = { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10, padding: '14px 16px' };
const OVERLAY = { position: 'fixed', inset: 0, background: '#000a', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 500 };
const MODAL = { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12, padding: 22, width: '90%', maxWidth: 480, maxHeight: '90vh', overflowY: 'auto' };
